import { AppBar, Drawer, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import React from "react";
import { isMobile } from "react-device-detect";
import { connect } from "react-redux";
import { Navigate, Outlet } from "react-router";
import { Link } from "react-router-dom";
import { getUserDetails } from "../api/auth";
import {
  createDefaultMarketplaceProfile,
  getUserProfile,
} from "../api/profile";
import { AppAlert } from "../components/form";
import { Header, Sidebar } from "../components/layouts";
import FlexBox from "../components/layouts/Flexbox";
import { MENU_ITEMS } from "../components/layouts/sidebar/menu-list";
import AlertDialog from "../components/modal/alert-dialog";
import { APP_COLORS, ENUM_USER_ROLE } from "../enums";
import { ROUTE_PATHS } from "../routes/paths";
import withRouter from "./withRouter";

const MAIN_LAYOUT_BACKGROUND =
  "linear-gradient(180deg, #03142b 0%, #183866 100%)";

const MainLayoutContainer = styled(Box)({
  background: MAIN_LAYOUT_BACKGROUND,
  height: "100vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
});
class MainLayoutHOC extends React.Component<any, any> {
  constructor(props) {
    super(props);
    this.setPageConfig = this.setPageConfig.bind(this);
    this.state = {
      drawerOpen: isMobile ? false : true,
      prevLocation: props.router.location,
      pageConfig: {
        title: "",
        headerActionItem: null,
        breadcrumbs: null,
        marketplaceProfileWarning: undefined,
        mainStyle: {},
      },
    };
  }

  componentDidUpdate(prevProps): void {
    const { location } = this.props.router;
    const profileData = this.props.profile;
    const userData = this.props.user;

    if (location.pathname !== this.state.prevLocation.pathname && isMobile) {
      this.setState({
        ...this.state,
        ...(this.state.drawerOpen && { drawerOpen: false }),
        prevLocation: location,
      });
    }

    if (
      this.state.marketplaceProfileWarning === undefined &&
      profileData &&
      !profileData?.marketplaceId &&
      userData?.role === ENUM_USER_ROLE.VENDOR
    ) {
      this.setState({
        marketplaceProfileWarning: true,
      });
    }
  }

  setPageConfig(config) {
    this.setState({
      pageConfig: config,
    });
  }

  handleDrawerToggle = () => {
    this.setState({
      drawerOpen: !this.state.drawerOpen,
    });
  };

  handleMarketplaceAlertClose = (isOk = undefined) => {
    this.setState(
      {
        marketplaceProfileWarning: false,
      },
      () => {
        this.props.dispatchCreateMarketplaceProfile();

        setTimeout(() => {
          if (isOk === "OK") {
            this.props.router.navigate(
              `${ROUTE_PATHS.MY_PROFILE}?type=profile`,
              { replace: true }
            );
          }
        }, 1000);
      }
    );
  };

  render() {
    const {
      pageConfig: { title, headerActionItem, breadcrumbs },
    } = this.state;
    const { isLoggedIn, profileId, isVerified, role } = this.props.user;
    const profileData = this.props.profile;
    const { location }: any = this.props?.router;

    if (
      !isLoggedIn &&
      location.pathname.toLowerCase().indexOf("/project/invitation") !== -1 &&
      location.search.toLowerCase().indexOf("email") !== -1
    ) {
      return (
        <Navigate
          to={`${ROUTE_PATHS.AUTH.SIGNUP_AMBASSADOR}${location?.search}`}
          replace
        />
      );
    }

    if (isLoggedIn && !profileId) {
      if (role === ENUM_USER_ROLE.AMBASSADOR) {
        return (
          <Navigate
            to={ROUTE_PATHS.AUTH.SIGNUP_MORE_ABOUT_AMBASSADOR_COMPANY}
            replace
          />
        );
      }

      if (role === ENUM_USER_ROLE.VENDOR) {
        return <Navigate to={ROUTE_PATHS.AUTH.SIGNUP_VENDOR} replace />;
      }
    }

    if (!isLoggedIn && !localStorage.getItem("token"))
      return <Navigate to={ROUTE_PATHS.AUTH.LOGIN} replace />;

    if (!isVerified && role === ENUM_USER_ROLE.AMBASSADOR)
      return <Navigate to={ROUTE_PATHS.SIGNUP_SUCCESS} replace />;

    if ((ENUM_USER_ROLE.VENDOR === role && !profileData?.licenseForMeeting) && (location.pathname !== ROUTE_PATHS.MY_PROFILE))
      return <Navigate to={ROUTE_PATHS.MY_PROFILE} replace />;

    if (
      [...MENU_ITEMS?.menu, ...MENU_ITEMS?.general].find(
        (r) => r.path === location.pathname
      ) &&
      ![...MENU_ITEMS?.menu, ...MENU_ITEMS?.general].find((r) => {
        return r.path === location.pathname &&
          !r.defaultDisabled.includes(this?.props?.user?.role)
          ? true
          : false;
      })
    ) {
      return <Navigate to={ROUTE_PATHS.MY_PROFILE} replace />;
    }

    const settingsPageURL = `${ROUTE_PATHS.SETTINGS}?type=payment`;
    const updateProfileURL = `${ROUTE_PATHS.MY_PROFILE}?type=profile`;

    const drawerWidth = 240;
    const drawerContent = (
      <Box
        width={drawerWidth}
        height="100%"
        bgcolor={MAIN_LAYOUT_BACKGROUND}
        pt={2}
        role="presentation"
        onClick={isMobile ? this.handleDrawerToggle : undefined}
        onKeyDown={isMobile ? this.handleDrawerToggle : undefined}
      >
        <Sidebar variant="drawer" />
      </Box>
    );

    return (
      <MainLayoutContainer p={{ xs: 1, sm: 1.5, lg: 1 }}>
        {isMobile ? (
          <AppBar
            position="sticky"
            elevation={0}
            sx={{
              background: "transparent",
              boxShadow: "none",
              flexShrink: 0,
              zIndex: (theme) => theme.zIndex.drawer - 1,
            }}
          >
            <Box sx={{ px: 1, py: 0.5 }}>
              <Header
                breadcrumbs={breadcrumbs}
                showMenuButton
                onMenuClick={this.handleDrawerToggle}
              />
            </Box>
          </AppBar>
        ) : (
          <Box sx={{ flexShrink: 0 }}>
            <Header breadcrumbs={breadcrumbs} />
          </Box>
        )}
        <Drawer
          variant="temporary"
          anchor="left"
          open={this.state.drawerOpen}
          onClose={this.handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: isMobile ? "block" : "none",
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              maxWidth: "85vw",
              boxSizing: "border-box",
              background: MAIN_LAYOUT_BACKGROUND,
              border: "none",
            },
          }}
        >
          {drawerContent}
        </Drawer>
        <Box
          sx={{
            flexGrow: 1,
            overflow: "hidden",
            display: "flex",
            minHeight: 0,
          }}
        >
          <FlexBox sx={{ width: "100%", height: "100%" }}>
            {!isMobile && (
              <Box maxWidth={120} sx={{ flexShrink: 0 }}>
                <Sidebar />
              </Box>
            )}
            <Box
              flexGrow={1}
              sx={{
                overflow: "auto",
                height: "100%",
              }}
            >
              <Box
                bgcolor={APP_COLORS.white}
                p={{ xs: 1.5, sm: 2, lg: 2.5 }}
                borderRadius={{ xs: 2, sm: 2.5, lg: "20px" }}
                minHeight="100%"
                style={this.state.pageConfig.mainStyle}
              >
                {profileData &&
                  !profileData?.round_table_package_12_rate &&
                  role === ENUM_USER_ROLE.AMBASSADOR && (
                    <AppAlert severity="warning">
                      Click <Link to={settingsPageURL}>here</Link> to add your
                      USD per 60 minute session
                    </AppAlert>
                  )}
                {profileData &&
                  !profileData?.marketplaceId &&
                  role === ENUM_USER_ROLE.VENDOR && (
                    <AppAlert severity="info" sx={{ mt: 2 }}>
                      Your profile is almost ready! Update it via the{" "}
                      <Link
                        to={updateProfileURL}
                        style={{ color: "white", textDecoration: "underline" }}
                      >
                        link
                      </Link>{" "}
                      to appear on the marketplace.
                    </AppAlert>
                  )}
                {(title || headerActionItem || breadcrumbs) && (
                  <Box
                    minHeight={50}
                    mb={1}
                    flexWrap={{ xs: "wrap", lg: "nowrap" }}
                    gap={{ xs: 1, lg: 0 }}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {title && (
                      <Typography variant="heading6-manrope-medium">
                        {title}
                      </Typography>
                    )}
                    {headerActionItem}
                  </Box>
                )}
                {this.state.marketplaceProfileWarning && (
                  <AlertDialog
                    title="Alert"
                    message={
                      "Start showcasing yourself on the marketplace! Ready to create your profile?"
                    }
                    onPrimaryActionClick={() =>
                      this.handleMarketplaceAlertClose("OK")
                    }
                    handleClose={this.handleMarketplaceAlertClose}
                  />
                )}
                <Outlet
                  context={{
                    setPageConfig: this.setPageConfig,
                  }}
                />
              </Box>
            </Box>
          </FlexBox>
        </Box>
      </MainLayoutContainer>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    user: state.auth.user,
    profile: state.profile?.data,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    dispatchGetUserProfile: () => dispatch(getUserProfile()),
    dispatchGetUserDetails: (payload) => dispatch(getUserDetails(payload)),
    dispatchCreateMarketplaceProfile: () =>
      dispatch(createDefaultMarketplaceProfile()),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(MainLayoutHOC));
